// §0.5.6 Provider-Created Red Text Flags + §0.5.7 System-Generated Clinical Flags
// Displayed at the top of every patient chart screen, below the patient name/header.
// Format: small red text, one line per active flag, stacking vertically.
// Soft-delete only — text and metadata retained permanently in audit history.

import { AddIcon, CloseIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  Flex,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import { useProfile } from '@contexts/user/ProfileContext'
import { useApp } from '@hb/react'
import { ProviderFlag, SaveProviderFlagArgs, WithId } from '@hb/shared'
import { useCallback, useMemo, useState } from 'react'
import { saveProviderFlag as saveProviderFlagFn } from '../../../backend/functions/gyn'
import { useAssessmentProviderFlags } from '../../../hooks/backend'

// Dismiss modal for system flags (requires a reason)
const SystemFlagDismissModal = ({
  flag,
  onClose,
  onConfirm,
}: {
  flag: WithId<ProviderFlag>
  onClose: () => void
  onConfirm: (reason: string) => Promise<void>
}) => {
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)
  return (
    <Modal isOpen onClose={onClose} size="sm">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="sm">Dismiss System Flag</ModalHeader>
        <ModalBody>
          <Text fontSize="sm" color="red.600" mb={2}>
            {flag.text}
          </Text>
          <Textarea
            placeholder="Brief reason for dismissal (required)"
            value={reason}
            onChange={e => setReason(e.target.value)}
            size="sm"
            rows={3}
          />
        </ModalBody>
        <ModalFooter gap={2}>
          <Button size="sm" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            colorScheme="red"
            isDisabled={!reason.trim()}
            isLoading={saving}
            onClick={async () => {
              setSaving(true)
              await onConfirm(reason.trim())
              setSaving(false)
              onClose()
            }}>
            Dismiss
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

// Individual flag row
const FlagRow = ({
  flag,
  assessmentId,
  patientId,
  practiceId,
}: {
  flag: WithId<ProviderFlag>
  assessmentId: string
  patientId: string
  practiceId: string
}) => {
  const { appName } = useApp()
  const [removing, setRemoving] = useState(false)
  const [showDismissModal, setShowDismissModal] = useState(false)

  const handleRemove = useCallback(
    async (removalReason?: string) => {
      setRemoving(true)
      const args: SaveProviderFlagArgs = {
        flagId: flag.id,
        patientId,
        assessmentId,
        practiceId,
        appName,
        source: flag.source,
        removalReason,
      }
      await saveProviderFlagFn(args)
      setRemoving(false)
    },
    [flag, patientId, assessmentId, practiceId, appName],
  )

  return (
    <>
      <Flex align="center" gap={1}>
        <Text fontSize="xs" color="red.600" fontFamily="Hero-New" fontWeight={600} lineHeight={1.3}>
          {flag.text}
        </Text>
        {removing ? (
          <Spinner size="xs" color="red.400" />
        ) : (
          <IconButton
            size="xs"
            variant="ghost"
            colorScheme="red"
            aria-label="Remove flag"
            icon={<CloseIcon w={2} h={2} />}
            onClick={() => {
              if (flag.source === 'system') {
                setShowDismissModal(true)
              } else {
                handleRemove()
              }
            }}
          />
        )}
      </Flex>
      {showDismissModal && (
        <SystemFlagDismissModal
          flag={flag}
          onClose={() => setShowDismissModal(false)}
          onConfirm={reason => handleRemove(reason)}
        />
      )}
    </>
  )
}

// Add flag inline form
const AddFlagForm = ({
  assessmentId,
  patientId,
  practiceId,
  onClose,
}: {
  assessmentId: string
  patientId: string
  practiceId: string
  onClose: () => void
}) => {
  const { appName } = useApp()
  const [text, setText] = useState('')
  const [saving, setSaving] = useState(false)

  const handleSave = useCallback(async () => {
    if (!text.trim()) return
    setSaving(true)
    const args: SaveProviderFlagArgs = {
      flagId: null,
      patientId,
      assessmentId,
      practiceId,
      appName,
      text: text.trim(),
      source: 'provider',
    }
    await saveProviderFlagFn(args)
    setSaving(false)
    onClose()
  }, [text, patientId, assessmentId, practiceId, appName, onClose])

  return (
    <Flex align="center" gap={2} mt={1}>
      <Input
        size="sm"
        placeholder="Flag text — free form"
        value={text}
        onChange={e => setText(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') handleSave()
          if (e.key === 'Escape') onClose()
        }}
        autoFocus
        maxW="300px"
        borderColor="red.200"
        _focus={{ borderColor: 'red.400', boxShadow: '0 0 0 1px var(--chakra-colors-red-400)' }}
      />
      <Button
        size="xs"
        colorScheme="red"
        isLoading={saving}
        isDisabled={!text.trim()}
        onClick={handleSave}>
        Add
      </Button>
      <Button size="xs" variant="ghost" onClick={onClose}>
        Cancel
      </Button>
    </Flex>
  )
}

interface PatientFlagsProps {
  assessmentId: string
  patientId: string
  /** readOnly = billing team view — no add/remove buttons */
  readOnly?: boolean
}

export const PatientFlags = ({ assessmentId, patientId, readOnly = false }: PatientFlagsProps) => {
  const { patientRef, practiceId } = useProfile()
  const [isAdding, setIsAdding] = useState(false)

  const { dataArr, loading } = useAssessmentProviderFlags(patientRef, assessmentId)

  const activeFlags = useMemo(() => (dataArr ?? []).filter(f => !f.removedAt), [dataArr])

  if (loading) return null
  if (activeFlags.length === 0 && readOnly) return null
  if (!practiceId) return null

  return (
    <Box
      bg={activeFlags.length > 0 ? 'red.50' : 'transparent'}
      _dark={{ bg: activeFlags.length > 0 ? 'red.900' : 'transparent' }}
      borderRadius={4}
      px={activeFlags.length > 0 || isAdding ? 3 : 0}
      pb={2}
      // mb={activeFlags.length > 0 || isAdding ? 2 : 0}>
    >
      <VStack align="stretch" spacing={0.5}>
        {activeFlags.map(flag => (
          <FlagRow
            key={flag.id}
            flag={flag}
            assessmentId={assessmentId}
            patientId={patientId}
            practiceId={practiceId}
          />
        ))}
      </VStack>

      {!readOnly && (
        <>
          {isAdding ? (
            <AddFlagForm
              assessmentId={assessmentId}
              patientId={patientId}
              practiceId={practiceId}
              onClose={() => setIsAdding(false)}
            />
          ) : (
            <Button
              size="sm"
              variant="ghost"
              colorScheme="red"
              leftIcon={<AddIcon w={2} h={2} />}
              onClick={() => setIsAdding(true)}
              mt={activeFlags.length > 0 ? 1 : 0}>
              Add flag
            </Button>
          )}
        </>
      )}
    </Box>
  )
}
